import { useEffect, useState } from 'react';
import { RestaurantImage } from '../../components/RestaurantImage.jsx';
import { color } from '../../theme/tokens.js';

/**
 * Tappable photo carousel.
 *
 * Falls back to a single placeholder image when Google Places has no photos, in
 * which case the navigation affordances are hidden entirely.
 *
 * @param {object} props
 * @param {import('../../lib/types.js').Restaurant} props.restaurant
 * @param {string[]} props.photoUrls
 * @param {boolean} [props.interactive]
 * @param {'top'|'bottom'} [props.indicatorPosition]
 */
export function PhotoCarousel({
  restaurant,
  photoUrls,
  photoAttributions = [],
  interactive = true,
  indicatorPosition = 'bottom',
}) {
  const [index, setIndex] = useState(0);

  // A shorter photo list (or a different restaurant) can leave index dangling.
  useEffect(() => {
    setIndex(0);
  }, [restaurant.id, photoUrls.length]);

  const hasMultiple = photoUrls.length > 1;
  const step = (delta) => (event) => {
    event.stopPropagation();
    setIndex((current) => (current + delta + photoUrls.length) % photoUrls.length);
  };

  return (
    <>
      <RestaurantImage restaurant={restaurant} src={photoUrls[index]} />

      {/* Google's terms: a Places photo is shown with its author, or with
          Google as the source when no author is given. */}
      {photoUrls.length > 0 && <PhotoCredit attribution={photoAttributions[index]} />}

      {interactive && hasMultiple && (
        <>
          <TapZone side="left" onClick={step(-1)} glyph="‹" />
          <TapZone side="right" onClick={step(1)} glyph="›" />

          <div
            style={{
              position: 'absolute',
              [indicatorPosition]: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 3,
              zIndex: 6,
            }}
          >
            {photoUrls.slice(0, 8).map((url, position) => (
              <div
                key={url}
                style={{
                  width: position === index ? 18 : 5,
                  height: 4,
                  borderRadius: 2,
                  background: position === index ? color.inkInverse : 'rgba(255,255,255,0.45)',
                  transition: 'width 0.2s',
                }}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
}

function PhotoCredit({ attribution }) {
  const name = attribution?.name;
  const uri = attribution?.sourceUri ?? attribution?.uri;
  const style = {
    position: 'absolute',
    right: 6,
    bottom: 6,
    zIndex: 7,
    padding: '1px 5px',
    borderRadius: 3,
    background: 'rgba(0,0,0,0.45)',
    color: 'rgba(255,255,255,0.9)',
    fontSize: 12,
    maxWidth: '88%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
  };
  if (uri) {
    return (
      <a
        href={uri}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        style={style}
      >
        {name ? `Photo: ${name} · Google Maps` : 'Photo · Google Maps'}
      </a>
    );
  }
  return <span style={style}>{name ? `Photo: ${name} · Google Maps` : 'Photo · Google Maps'}</span>;
}

/**
 * A wide invisible hit area with a small chevron, so advancing photos doesn't
 * demand precision on a phone.
 */
function TapZone({ side, onClick, glyph }) {
  return (
    <div
      onClick={onClick}
      style={{
        position: 'absolute',
        [side]: 0,
        top: 0,
        bottom: 0,
        width: '38%',
        zIndex: 5,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
        padding: '0 8px',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.32)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color.inkInverse,
          fontSize: 17,
          lineHeight: 1,
          userSelect: 'none',
        }}
      >
        {glyph}
      </div>
    </div>
  );
}
